/**
 * PixiJS WebGL 世界渲染器。
 * 地形/树/物件烘焙成贴图；角色与 HUD 用对象池按帧同步。
 */

import {
  Application,
  Container,
  Graphics,
  Rectangle,
  RenderTexture,
  Sprite,
  Text,
  Texture,
  TextureStyle,
  TilingSprite
} from 'pixi.js'
import {
  MAP_WIDTH,
  MAP_HEIGHT,
  TILE_SIZE,
  TILE,
  TILE_COLORS,
  terrainSpriteAt,
  objectSpriteAt,
  treeSpriteAt,
  isOnMap,
  WATER_TERRAIN_SPRITE,
  WATER_FILL_COLOR
} from '@/game/worldMap'
import { spriteRect } from '@/game/pico8Atlas'
import { drawPixelPerson } from '@/game/pixelCharacter'
import { drawPixelChest } from '@/game/pixelChest'
import { normalizeOutfit } from '@/game/outfit'
import sheetUrl from '@/assets/kenney/Pico8City/Tilemap/tilemap.png'

const CHAR_SIZE = 16
const WATER_PAD_TILES = 48
const FONT_FAMILY = 'Cubic_11, Avenir, Helvetica, Arial, sans-serif'

try {
  TextureStyle.defaultOptions.scaleMode = 'nearest'
} catch (e) {
  /* Pixi 旧版无 TextureStyle 时忽略 */
}

function parseColor (hex, fallback = 0x3d8fd4) {
  if (typeof hex !== 'string') return fallback
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim())
  if (!m) return fallback
  return Number.parseInt(m[1], 16)
}

function canvasTexture (w, h, paint) {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingEnabled = false
  paint(ctx)
  const texture = Texture.from(canvas)
  if (texture.source) texture.source.scaleMode = 'nearest'
  return texture
}

function loadImage (src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

function wrapLines (text, maxWidth, font) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  ctx.font = font
  const lines = []
  for (const para of String(text).split('\n')) {
    let line = ''
    for (const ch of para) {
      const next = line + ch
      if (ctx.measureText(next).width > maxWidth && line) {
        lines.push(line)
        line = ch
      } else {
        line = next
      }
    }
    if (line) lines.push(line)
  }
  return lines.length ? lines : ['']
}

export async function createPixiWorldRenderer (hostEl) {
  const app = new Application()
  await app.init({
    background: parseColor(WATER_FILL_COLOR),
    antialias: false,
    roundPixels: true,
    autoDensity: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    preference: 'webgl'
  })

  const view = app.canvas
  view.className = 'pixel-world__canvas'
  view.style.width = '100%'
  view.style.height = '100%'
  view.style.display = 'block'
  view.style.imageRendering = 'pixelated'
  hostEl.appendChild(view)
  app.stage.eventMode = 'none'

  const sheetImg = await loadImage(sheetUrl)
  const sheetBase = Texture.from(sheetImg)
  if (sheetBase.source) sheetBase.source.scaleMode = 'nearest'

  const tileTexCache = new Map()
  function tileTexture (index) {
    if (index < 0) return null
    let tex = tileTexCache.get(index)
    if (tex) return tex
    const rect = spriteRect(index)
    if (!rect) return null
    tex = new Texture({
      source: sheetBase.source,
      frame: new Rectangle(rect.sx, rect.sy, rect.sw, rect.sh)
    })
    tileTexCache.set(index, tex)
    return tex
  }

  function makeTileSprite (index, x, y, fallbackType) {
    const tex = tileTexture(index)
    if (tex) {
      const sprite = new Sprite(tex)
      sprite.x = x
      sprite.y = y
      sprite.width = TILE_SIZE
      sprite.height = TILE_SIZE
      sprite.roundPixels = true
      return sprite
    }
    const g = new Graphics()
    const colors = TILE_COLORS[fallbackType] || TILE_COLORS[TILE.GRASS]
    g.rect(0, 0, TILE_SIZE, TILE_SIZE)
    g.fill(parseColor(colors.base, 0x5ec878))
    g.x = x
    g.y = y
    return g
  }

  function bakeLayer (buildFn) {
    const container = new Container()
    buildFn(container)
    const rt = RenderTexture.create({
      width: MAP_WIDTH * TILE_SIZE,
      height: MAP_HEIGHT * TILE_SIZE,
      antialias: false,
      scaleMode: 'nearest'
    })
    app.renderer.render({ container, target: rt })
    container.destroy({ children: true })
    const sprite = new Sprite(rt)
    sprite.roundPixels = true
    return sprite
  }

  const world = new Container()
  world.eventMode = 'none'

  const waterTex = tileTexture(WATER_TERRAIN_SPRITE)
  if (waterTex) {
    const pad = WATER_PAD_TILES * TILE_SIZE
    const water = new TilingSprite({
      texture: waterTex,
      width: MAP_WIDTH * TILE_SIZE + pad * 2,
      height: MAP_HEIGHT * TILE_SIZE + pad * 2
    })
    water.position.set(-pad, -pad)
    world.addChild(water)
  }

  world.addChild(bakeLayer((layer) => {
    for (let my = 0; my < MAP_HEIGHT; my++) {
      for (let mx = 0; mx < MAP_WIDTH; mx++) {
        layer.addChild(makeTileSprite(
          terrainSpriteAt(mx, my),
          mx * TILE_SIZE,
          my * TILE_SIZE,
          TILE.GRASS
        ))
      }
    }
  }))

  world.addChild(bakeLayer((layer) => {
    for (let my = 0; my < MAP_HEIGHT; my++) {
      for (let mx = 0; mx < MAP_WIDTH; mx++) {
        if (!isOnMap(mx, my)) continue
        const idx = treeSpriteAt(mx, my)
        if (idx < 0) continue
        layer.addChild(makeTileSprite(idx, mx * TILE_SIZE, my * TILE_SIZE))
      }
    }
  }))

  const hoverGfx = new Graphics()
  world.addChild(hoverGfx)

  const entityLayer = new Container()
  entityLayer.sortableChildren = true
  world.addChild(entityLayer)

  world.addChild(bakeLayer((layer) => {
    for (let my = 0; my < MAP_HEIGHT; my++) {
      for (let mx = 0; mx < MAP_WIDTH; mx++) {
        if (!isOnMap(mx, my)) continue
        const idx = objectSpriteAt(mx, my)
        if (idx < 0) continue
        layer.addChild(makeTileSprite(idx, mx * TILE_SIZE, my * TILE_SIZE))
      }
    }
  }))

  const hud = new Container()
  hud.eventMode = 'none'
  app.stage.addChild(world)
  app.stage.addChild(hud)

  const personTexCache = new Map()
  const chestTex = canvasTexture(CHAR_SIZE, CHAR_SIZE, (ctx) => {
    drawPixelChest(ctx, 0, 0, 1, false)
  })

  function personTexture (opts) {
    const walk = ((Math.floor(opts.walkPhase || 0) % 4) + 4) % 4
    const outfit = opts.outfit ? normalizeOutfit(opts.outfit) : null
    const key = [
      opts.role || '',
      opts.username || '',
      opts.facing || 'down',
      walk,
      opts.isSelf ? 1 : 0,
      opts.showHat ? 1 : 0,
      outfit ? JSON.stringify(outfit) : ''
    ].join('|')
    let tex = personTexCache.get(key)
    if (tex) return tex
    tex = canvasTexture(CHAR_SIZE, CHAR_SIZE, (ctx) => {
      drawPixelPerson(ctx, 0, 0, 1, {
        role: opts.role,
        username: opts.username,
        facing: opts.facing,
        walkPhase: walk,
        isSelf: opts.isSelf,
        showHat: opts.showHat,
        outfit
      })
    })
    personTexCache.set(key, tex)
    return tex
  }

  const entityPool = new Map()
  const hudPool = new Map()

  function acquire (pool, key, factory, parent) {
    let item = pool.get(key)
    if (!item) {
      item = factory()
      parent.addChild(item.root)
      pool.set(key, item)
    }
    item.used = true
    item.root.visible = true
    return item
  }

  function sweep (pool) {
    for (const item of pool.values()) {
      if (!item.used) item.root.visible = false
      item.used = false
    }
  }

  function makeEntity () {
    const root = new Container()
    const sprite = new Sprite()
    sprite.roundPixels = true
    root.addChild(sprite)
    return { root, sprite }
  }

  function makeHud () {
    const root = new Container()
    const label = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 11,
        fontWeight: 'bold',
        fill: '#ffffff',
        stroke: { color: 'rgba(0,0,0,0.72)', width: 3, join: 'round' },
        align: 'center'
      }
    })
    label.anchor.set(0.5, 1)
    const badge = new Text({
      text: '',
      style: { fontFamily: 'sans-serif', fontSize: 12, align: 'center' }
    })
    badge.anchor.set(0.5, 1)
    const action = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 10,
        fontWeight: 'bold',
        fill: '#fff3a0',
        stroke: { color: 'rgba(0,0,0,0.72)', width: 3, join: 'round' },
        align: 'center'
      }
    })
    action.anchor.set(0.5, 1)
    const arrow = new Graphics()
    const bubble = new Graphics()
    const bubbleText = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 11,
        fill: '#2a2a32',
        align: 'left'
      }
    })
    root.addChild(badge, label, action, arrow, bubble, bubbleText)
    return { root, label, badge, action, arrow, bubble, bubbleText }
  }

  function drawArrow (g, size) {
    g.clear()
    const half = size * 0.52
    g.moveTo(0, size)
    g.lineTo(-half, 0)
    g.lineTo(half, 0)
    g.closePath()
    g.fill(0xf4c430)
    g.stroke({ width: Math.max(1.2, size * 0.1), color: 0x9a7209 })
  }

  function layoutSpeech (item, text, isSelf, centerX, tipY, tilePx) {
    const fontSize = Math.max(9, Math.round(tilePx * 0.28 + 4))
    const font = `${fontSize}px ${FONT_FAMILY}`
    const maxW = Math.max(56, Math.min(200, tilePx * 3.4))
    const lines = wrapLines(text, maxW - 12, font)
    const lineH = fontSize + 3
    const padX = 6
    const padY = 5
    const measure = document.createElement('canvas').getContext('2d')
    measure.font = font
    let bw = 0
    for (const ln of lines) bw = Math.max(bw, measure.measureText(ln).width)
    bw = Math.ceil(bw) + padX * 2
    const bh = lines.length * lineH + padY * 2
    const tailH = Math.max(5, Math.round(tilePx * 0.09))
    const tailW = Math.max(4, Math.floor(tailH * 0.85))
    const x = Math.round(centerX - bw / 2)
    const y = Math.round(tipY - tailH - bh)
    const r = Math.min(8, Math.round(tilePx * 0.09))

    item.bubble.clear()
    item.bubble.moveTo(centerX, tipY)
    item.bubble.lineTo(centerX - tailW, y + bh)
    item.bubble.lineTo(x + r, y + bh)
    item.bubble.arcTo(x, y + bh, x, y + bh - r, r)
    item.bubble.lineTo(x, y + r)
    item.bubble.arcTo(x, y, x + r, y, r)
    item.bubble.lineTo(x + bw - r, y)
    item.bubble.arcTo(x + bw, y, x + bw, y + r, r)
    item.bubble.lineTo(x + bw, y + bh - r)
    item.bubble.arcTo(x + bw, y + bh, x + bw - r, y + bh, r)
    item.bubble.lineTo(centerX + tailW, y + bh)
    item.bubble.closePath()
    item.bubble.fill({ color: isSelf ? 0x4a90e2 : 0xffffff, alpha: 0.94 })
    item.bubble.stroke({
      width: 1,
      color: isSelf ? 0xffffff : 0x3c3c46,
      alpha: isSelf ? 0.85 : 0.35
    })

    item.bubbleText.visible = true
    item.bubbleText.style.fontSize = fontSize
    item.bubbleText.style.fill = isSelf ? '#ffffff' : '#2a2a32'
    item.bubbleText.text = lines.join('\n')
    item.bubbleText.x = x + padX
    item.bubbleText.y = y + padY
  }

  let cssW = 0
  let cssH = 0
  let viewW = 1
  let viewH = 1

  function applyCamera (camera, viewTilesW, viewTilesH) {
    viewW = Math.max(1, viewTilesW)
    viewH = Math.max(1, viewTilesH)
    if (cssW < 1 || cssH < 1) return
    const tilePxW = cssW / viewW
    const tilePxH = cssH / viewH
    world.scale.set(tilePxW / TILE_SIZE, tilePxH / TILE_SIZE)
    world.position.set(-camera.cx * tilePxW, -camera.cy * tilePxH)
  }

  function worldToScreen (wx, wy, camera) {
    const tilePxW = cssW / viewW
    const tilePxH = cssH / viewH
    return {
      x: (wx - camera.cx) * tilePxW,
      y: (wy - camera.cy) * tilePxH,
      tilePxW,
      tilePxH
    }
  }

  return {
    canvas: view,
    resize (w, h) {
      cssW = Math.max(1, Math.floor(w))
      cssH = Math.max(1, Math.floor(h))
      app.renderer.resize(cssW, cssH)
    },
    onTick (fn) {
      app.ticker.add(() => fn(performance.now()))
    },
    sync (scene) {
      const camera = scene.camera || { cx: 0, cy: 0 }
      applyCamera(camera, scene.viewTilesW, scene.viewTilesH)
      const tilePx = Math.min(cssW / viewW, cssH / viewH)

      hoverGfx.clear()
      const hover = scene.hoverTile
      if (hover && Number.isFinite(hover.x) && Number.isFinite(hover.y)) {
        hoverGfx.rect(hover.x * TILE_SIZE, hover.y * TILE_SIZE, TILE_SIZE, TILE_SIZE)
        hoverGfx.fill({ color: 0xffffff, alpha: 0.25 })
      }

      for (const ent of scene.entities || []) {
        const item = acquire(entityPool, ent.key, makeEntity, entityLayer)
        item.root.zIndex = ent.zIndex || 0
        item.root.x = ent.x * TILE_SIZE
        item.root.y = ent.y * TILE_SIZE
        item.sprite.texture = ent.kind === 'chest' ? chestTex : personTexture(ent.person || {})
        const scale = TILE_SIZE / CHAR_SIZE
        item.sprite.scale.set(scale)
        item.sprite.x = (TILE_SIZE - CHAR_SIZE * scale) / 2
        item.sprite.y = (TILE_SIZE - CHAR_SIZE * scale) / 2 + (ent.kind === 'chest' ? 2 : 0)
      }
      sweep(entityPool)

      for (const ov of scene.overlays || []) {
        const item = acquire(hudPool, ov.key, makeHud, hud)
        const scr = worldToScreen(ov.x, ov.y, camera)
        const cx = scr.x + scr.tilePxW / 2
        const top = scr.y
        const fontSize = Math.max(8, Math.round(tilePx * 0.18 + 6))

        item.badge.visible = !!ov.emoji
        if (ov.emoji) {
          item.badge.text = ov.emoji
          item.badge.style.fontSize = Math.max(10, fontSize)
          item.badge.x = cx
          item.badge.y = top - fontSize * 0.2
        }

        item.label.visible = !!ov.label
        if (ov.label) {
          item.label.text = ov.label
          item.label.style.fontSize = fontSize
          item.label.style.fill = ov.labelColor || '#ffffff'
          item.label.x = cx
          item.label.y = top - (ov.emoji ? fontSize * 1.15 : 2)
        }

        let cursorY = top - (ov.emoji ? fontSize * 1.15 : 2) - fontSize - 4
        const bounce = Math.sin((scene.animFrame || 0) * 0.12) * 3

        item.arrow.clear()
        item.arrow.visible = !!ov.showArrow
        if (ov.showArrow) {
          const size = Math.max(10, tilePx * 0.32)
          drawArrow(item.arrow, size)
          item.arrow.x = cx
          item.arrow.y = cursorY - size + bounce
          cursorY = item.arrow.y - 6
        }

        item.action.visible = !!ov.actionLabel
        if (ov.actionLabel) {
          item.action.text = ov.actionLabel
          item.action.style.fontSize = Math.max(9, fontSize - 1)
          item.action.x = cx
          item.action.y = cursorY
          cursorY -= fontSize + 2
        }

        if (ov.speech) {
          layoutSpeech(item, ov.speech, !!ov.speechSelf, cx, cursorY, tilePx)
          item.bubble.visible = true
        } else {
          item.bubble.clear()
          item.bubble.visible = false
          item.bubbleText.visible = false
        }
      }
      sweep(hudPool)
    },
    destroy () {
      app.ticker.stop()
      app.destroy(true, { children: true })
      if (view.parentNode) view.parentNode.removeChild(view)
      personTexCache.clear()
      entityPool.clear()
      hudPool.clear()
    }
  }
}
